import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, Key, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { apiKeysApi } from '@/api/api-keys';
import { unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { ApiKeyScope, BotApiKeyWithToken } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function scopeBadgeVariant(scope: ApiKeyScope): 'secondary' | 'info' | 'outline' {
  if (scope === 'widget') return 'secondary';
  if (scope === 'mcp') return 'info';
  return 'outline';
}

export function ApiKeysTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentBot, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const [mintOpen, setMintOpen] = React.useState(false);
  const [reveal, setReveal] = React.useState<BotApiKeyWithToken | null>(null);

  const keysQuery = useQuery({
    queryKey: ['org', 'api-keys', currentBotId],
    queryFn: () => apiKeysApi.listForBot(currentBotId!),
    enabled: !!currentBotId,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['org', 'api-keys', currentBotId] });
  };

  const revokeMutation = useMutation({
    mutationFn: (keyHash: string) => apiKeysApi.revoke(currentBotId!, keyHash),
    onSuccess: () => {
      toast.success(t('organization.apiKeys.revokedToast'));
      invalidate();
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const revealMutation = useMutation({
    mutationFn: async (k: { keyHash: string; label: string }) => {
      const res = await apiKeysApi.reveal(currentBotId!, k.keyHash);
      return { token: res.token, label: k.label, keyHash: k.keyHash };
    },
    onSuccess: (data) => {
      if (!data.token) {
        toast.error(
          t('organization.apiKeys.revealMissing', {
            defaultValue:
              'No plaintext stored for this key (minted before PR-Widget-2). Revoke + create a new one.',
          }),
        );
        return;
      }
      setReveal({
        keyHash: data.keyHash,
        orgId: '',
        botId: currentBotId!,
        label: data.label,
        scope: 'full',
        createdAt: 0,
        createdBy: '',
        token: data.token,
      });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t('organization.apiKeys.selectBot')}
        </CardContent>
      </Card>
    );
  }

  const keys = keysQuery.data?.items ?? [];
  const activeKeys = keys.filter((k) => !k.revokedAt);
  const revokedKeys = keys.filter((k) => k.revokedAt);
  const botLabel = currentBot?.name ?? currentBotId;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">{t('organization.apiKeys.title')}</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            <Trans
              i18nKey="chat.browse.botInfo"
              values={{ bot: botLabel, count: activeKeys.length }}
              components={{ mono: <span className="font-mono" /> }}
            />{' '}
            ·{' '}
            {t(activeKeys.length > 1 ? 'organization.apiKeys.active_plural' : 'organization.apiKeys.active', { count: activeKeys.length })}
            {revokedKeys.length > 0 ? (
              <>
                {' '}
                ·{' '}
                {t(revokedKeys.length > 1 ? 'organization.apiKeys.revoked_plural' : 'organization.apiKeys.revoked', { count: revokedKeys.length })}
              </>
            ) : null}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setMintOpen(true)}
          disabled={!canManage}
          className="gap-1.5"
          title={canManage ? t('organization.apiKeys.newTooltip') : t('organization.apiKeys.newRestricted')}
        >
          <Plus className="h-4 w-4" />
          {t('organization.apiKeys.new')}
        </Button>
      </CardHeader>
      <CardContent>
        {keysQuery.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </p>
        ) : keysQuery.error ? (
          <p className="text-sm text-destructive">{unwrapError(keysQuery.error)}</p>
        ) : keys.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            <Key className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <Trans i18nKey="organization.apiKeys.empty" components={{ code: <code className="mx-1" /> }} />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('organization.apiKeys.headers.label')}</TableHead>
                <TableHead>{t('organization.apiKeys.headers.scope', { defaultValue: 'Scope' })}</TableHead>
                <TableHead>{t('organization.apiKeys.headers.fingerprint')}</TableHead>
                <TableHead>{t('organization.apiKeys.headers.created')}</TableHead>
                <TableHead>{t('organization.apiKeys.headers.lastUsed')}</TableHead>
                <TableHead>{t('organization.apiKeys.headers.status')}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.keyHash} className={k.revokedAt ? 'opacity-60' : undefined}>
                  <TableCell className="font-medium">{k.label}</TableCell>
                  <TableCell>
                    <Badge variant={scopeBadgeVariant(k.scope)} className="text-[10px]">
                      {k.scope}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">
                    {k.keyHash.slice(0, 12)}…{k.keyHash.slice(-8)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    {k.revokedAt ? (
                      <Badge variant="destructive" className="text-[10px]">
                        {t('organization.apiKeys.revoked_label')}
                      </Badge>
                    ) : (
                      <Badge variant="success" className="text-[10px]">
                        {t('organization.apiKeys.active_label')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" variant="ghost" disabled={!canManage}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => revealMutation.mutate({ keyHash: k.keyHash, label: k.label })}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {t('organization.apiKeys.reveal_action', { defaultValue: 'Reveal token' })}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => {
                            if (window.confirm(t('organization.apiKeys.confirmRevoke', { label: k.label }))) {
                              revokeMutation.mutate(k.keyHash);
                            }
                          }}
                        >
                          {t('organization.apiKeys.revoke')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <MintKeyDialog
        open={mintOpen}
        botId={currentBotId}
        onOpenChange={setMintOpen}
        onMinted={(k) => {
          invalidate();
          setReveal(k);
        }}
      />
      {reveal ? (
        <RevealKeyDialog token={reveal.token} label={reveal.label} onClose={() => setReveal(null)} />
      ) : null}
    </Card>
  );
}

function MintKeyDialog({
  open,
  botId,
  onOpenChange,
  onMinted,
}: {
  open: boolean;
  botId: string;
  onOpenChange: (open: boolean) => void;
  onMinted: (key: BotApiKeyWithToken) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [label, setLabel] = React.useState('');
  const [scope, setScope] = React.useState<ApiKeyScope>('full');
  React.useEffect(() => {
    if (open) {
      setLabel('');
      setScope('full');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => apiKeysApi.mint(botId, label.trim(), scope),
    onSuccess: (data) => {
      onOpenChange(false);
      onMinted(data);
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!label.trim()) return;
            mutation.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{t('organization.apiKeys.newDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('organization.apiKeys.newDialog.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="key-label">{t('organization.apiKeys.newDialog.label')}</Label>
            <Input
              id="key-label"
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="prod-curl"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('organization.apiKeys.newDialog.labelHint')}
            </p>
          </div>
          <div>
            <Label htmlFor="key-scope">
              {t('organization.apiKeys.newDialog.scope', { defaultValue: 'Scope' })}
            </Label>
            <select
              id="key-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as ApiKeyScope)}
              className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="full">
                {t('organization.apiKeys.newDialog.scopeFull', { defaultValue: 'full — server-to-server (all endpoints)' })}
              </option>
              <option value="widget">
                {t('organization.apiKeys.newDialog.scopeWidget', { defaultValue: 'widget — public browser widget (chat only)' })}
              </option>
              <option value="mcp">
                {t('organization.apiKeys.newDialog.scopeMcp', { defaultValue: 'mcp — MCP client (Claude Code, Gemini CLI…)' })}
              </option>
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('organization.apiKeys.newDialog.scopeHint', {
                defaultValue: 'Widget keys are restricted to /v1/chat/* and require an allowed Origin.',
              })}
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('organization.apiKeys.newDialog.generate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RevealKeyDialog({
  token,
  label,
  onClose,
}: {
  token: string;
  label: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('organization.apiKeys.reveal.titleConsult', {
              defaultValue: 'API key « {{label}} »',
              label,
            })}
          </DialogTitle>
          <DialogDescription>
            {t('organization.apiKeys.reveal.subtitleConsult', {
              defaultValue:
                'Plaintext token below. Anyone with this string can call /v1/chat/* — store it on your server, never in client-side code (except inside the widget bundle data-api-key attribute, which is the intended use for widget-scoped keys).',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 p-3">
            <code className="block break-all font-mono text-xs">{token}</code>
          </div>
          <Button
            variant="outline"
            className="w-full gap-2"
            onClick={() => {
              void navigator.clipboard.writeText(token);
              setCopied(true);
              toast.success(t('organization.apiKeys.reveal.tokenCopied'));
            }}
          >
            <Copy className="h-4 w-4" />
            {copied ? t('organization.apiKeys.reveal.copied') : t('organization.apiKeys.reveal.copy')}
          </Button>
          <details className="rounded-md border bg-muted/20 p-3 text-xs">
            <summary className="cursor-pointer font-medium">{t('organization.apiKeys.reveal.curlExample')}</summary>
            <pre className="mt-2 overflow-auto text-[11px]">
              {`curl -X POST $API_URL/v1/chat/sessions \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{"kind":"support"}'`}
            </pre>
          </details>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t('organization.apiKeys.reveal.acknowledge')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
